import * as React from 'react';
import { Counsellor } from '../../types/counsellor';
import { 
  Typography, 
  Checkbox, 
  FormGroup, 
  FormControlLabel, 
  Grid,
  Box,
  Chip,
  TextField,
  IconButton
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import { fetchPublicLookups } from '../../api/lookups';
import { Lookup } from '../../types/lookup';
import AvailabilitySlotEditor, { AvailabilitySlot } from '../shared/AvailabilitySlotEditor';

interface ProfessionalInformationProps {
  counsellor: Counsellor;
  onUpdate?: () => void;
  availabilityLookups?: Lookup[];
  sessionTypesLookups?: Lookup[];
  modalityLookups?: Lookup[];
}

const ProfessionalInformation: React.FC<ProfessionalInformationProps> = ({
  counsellor,
  onUpdate,
  availabilityLookups: propAvailabilityLookups,
  sessionTypesLookups: propSessionTypesLookups,
  modalityLookups: propModalityLookups
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [availabilityLookups, setAvailabilityLookups] = React.useState<Lookup[]>([]);
  const [sessionTypesLookups, setSessionTypesLookups] = React.useState<Lookup[]>([]);
  const [modalityLookups, setModalityLookups] = React.useState<Lookup[]>([]);
  const [experienceTrainingLookups, setExperienceTrainingLookups] = React.useState<Lookup[]>([]);
  const [languageLookups, setLanguageLookups] = React.useState<Lookup[]>([]);
  
  const [selectedAvailability, setSelectedAvailability] = React.useState<string[]>([]);
  const [selectedSessionTypes, setSelectedSessionTypes] = React.useState<string[]>([]);
  const [selectedModality, setSelectedModality] = React.useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = React.useState<string[]>([]);
  const [maxClients, setMaxClients] = React.useState<number>(0);
  const [availabilitySlots, setAvailabilitySlots] = React.useState<AvailabilitySlot[]>([]);
  const [selectedExperienceOrTraining, setSelectedExperienceOrTraining] = React.useState<string[]>([]);
  const [customTagInput, setCustomTagInput] = React.useState<string>('');
  const [completedHours, setCompletedHours] = React.useState<number>(0);
  const [digitalSessions, setDigitalSessions] = React.useState<number>(0);
  const [previousSessions, setPreviousSessions] = React.useState<number>(0);
  const [loadingHours, setLoadingHours] = React.useState<boolean>(true);
  
  // Fetch completed session hours
  React.useEffect(() => {
    const fetchCompletedHours = async () => {
      try {
        setLoadingHours(true);
        const api = (await import('../../utils/api')).default;
        const response = await api.get(`/counsellors/${counsellor._id}/completed-hours`);
        setCompletedHours(response.data.completedHours || 0);
        setDigitalSessions(response.data.digitalSessions || 0);
        setPreviousSessions(response.data.previousSessions || 0);
      } catch (error) {
        console.error('Error fetching completed hours:', error);
      } finally {
        setLoadingHours(false);
      }
    };

    if (counsellor._id) {
      fetchCompletedHours();
    }
  }, [counsellor._id]);

  React.useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [availability, sessionTypes, modality, experienceTraining, languages] = await Promise.all([
          fetchPublicLookups('availability'),
          fetchPublicLookups('sessionTypes'),
          fetchPublicLookups('modality'),
          fetchPublicLookups('experienceTraining'),
          fetchPublicLookups('language')
        ]);

        setAvailabilityLookups(availability);
        setSessionTypesLookups(sessionTypes);
        setModalityLookups(modality);
        setExperienceTrainingLookups(experienceTraining);
        setLanguageLookups(languages);
      } catch (error) {
        console.error('Error fetching lookups:', error);
      }
    };

    fetchLookups();
  }, []);

  // Helper function to expand 'All' or 'Any' to complete list
  const expandAllOrAny = (values: string[], lookups: Lookup[]): string[] => {
    if (!values || values.length === 0) return [];
    
    // Check if array contains 'All' or 'Any' (case-insensitive)
    const hasAllOrAny = values.some(v => 
      v.toLowerCase() === 'all' || v.toLowerCase() === 'any'
    );
    
    if (hasAllOrAny) {
      // Return all active lookup values
      return lookups
        .filter(lookup => lookup.active !== false)
        .map(lookup => lookup.value);
    }
    
    return values;
  };

  React.useEffect(() => {
    if (counsellor && modalityLookups.length > 0 && sessionTypesLookups.length > 0 && availabilityLookups.length > 0) {
      // For modality, ensure it's always an array and sync with application data
      const counsellorModality = Array.isArray(counsellor.modality) 
        ? counsellor.modality 
        : counsellor.modality 
          ? [counsellor.modality] 
          : [];
      
      const applicationModality = counsellor.application?.counselling?.modality ?? [];
      const applicationSessionTypes = counsellor.application?.counselling?.sessionTypes ?? [];
      const applicationAvailability = counsellor.application?.counselling?.availability ?? [];
      
      // Expand 'All' or 'Any' to full list of options
      const expandedModality = expandAllOrAny(
        counsellorModality.length > 0 ? counsellorModality : applicationModality,
        modalityLookups
      );
      const expandedSessionTypes = expandAllOrAny(
        counsellor.sessionTypes && counsellor.sessionTypes.length > 0 ? counsellor.sessionTypes : applicationSessionTypes,
        sessionTypesLookups
      );
      const expandedAvailability = expandAllOrAny(
        counsellor.availability && counsellor.availability.length > 0 ? counsellor.availability : applicationAvailability,
        availabilityLookups
      );
      
      // Ensure we always pass string arrays to setState
      setSelectedModality(expandedModality);
      setSelectedSessionTypes(expandedSessionTypes);
      setSelectedAvailability(expandedAvailability);
      setMaxClients(counsellor.maxClients ?? 0);
      setAvailabilitySlots(counsellor.availabilitySlots || []);

      // Initialize experienceOrTraining: prefer top-level, fall back to application data
      const topLevelExp = counsellor.experienceOrTraining || [];
      const applicationExp = counsellor.application?.counselling?.experienceOrTraining || [];
      setSelectedExperienceOrTraining(topLevelExp.length > 0 ? topLevelExp : applicationExp);

      // Initialize languages: prefer top-level, fall back to application data
      const topLevelLanguages = counsellor.languages || [];
      const applicationLanguages = counsellor.application?.counselling?.languages || [];
      setSelectedLanguages(topLevelLanguages.length > 0 ? topLevelLanguages : applicationLanguages);
    }
  }, [counsellor, modalityLookups, sessionTypesLookups, availabilityLookups]);

  const handleSave = async () => {
    try {
      if (!counsellor?._id) return;

      const response = await fetch(`/api/counsellors/${counsellor._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          modality: selectedModality,
          sessionTypes: selectedSessionTypes,
          availability: selectedAvailability,
          availabilitySlots: availabilitySlots,
          experienceOrTraining: selectedExperienceOrTraining,
          languages: selectedLanguages,
          maxClients: maxClients
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update profile');
      }

      setIsEditing(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const getLookupLabel = (value: string, lookups: Lookup[]): string => {
    const lookup = lookups.find(l => l.value === value);
    if (lookup) {
      return lookup.displayName;
    }
    // Handle special case for 'all' value to display as 'All'
    if (value === 'all') {
      return 'All';
    }
    return value;
  };

  const renderChipList = (values: string[], lookups: Lookup[]) => {
    return values.map((value) => (
      <Chip
        key={value}
        label={getLookupLabel(value, lookups)}
        size="small"
        sx={{ 
          backgroundColor: 'rgba(100, 181, 246, 0.2)',
          color: 'rgba(255, 255, 255, 0.9)',
          border: '1px solid rgba(100, 181, 246, 0.3)',
          mb: { xs: 1, sm: 0.5 },
          mr: { xs: 1, sm: 0.5 }
        }}
      />
    ));
  };

  const renderModalitySelect = () => {
    if (isEditing) {
      return (
        <FormGroup>
          {modalityLookups.map((lookup) => (
            <FormControlLabel
              key={lookup.value}
              control={
                <Checkbox
                  checked={selectedModality.includes(lookup.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedModality([...selectedModality, lookup.value]);
                    } else {
                      setSelectedModality(selectedModality.filter(m => m !== lookup.value));
                    }
                  }}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-checked': {
                      color: 'rgba(100, 181, 246, 0.9)'
                    }
                  }}
                />
              }
              label={lookup.displayName}
              sx={{
                mb: { xs: 1.5, sm: 1 },
                ml: 0,
                '& .MuiFormControlLabel-label': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { xs: '0.9375rem', sm: '1rem' }
                }
              }}
            />
          ))}
        </FormGroup>
      );
    }
    return renderChipList(selectedModality, modalityLookups);
  };

  const renderSessionTypesSelect = () => {
    if (isEditing) {
      return (
        <FormGroup>
          {sessionTypesLookups.map((lookup) => (
            <FormControlLabel
              key={lookup.value}
              control={
                <Checkbox
                  checked={selectedSessionTypes.includes(lookup.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedSessionTypes([...selectedSessionTypes, lookup.value]);
                    } else {
                      setSelectedSessionTypes(selectedSessionTypes.filter(st => st !== lookup.value));
                    }
                  }}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-checked': {
                      color: 'rgba(100, 181, 246, 0.9)'
                    }
                  }}
                />
              }
              label={lookup.displayName}
              sx={{
                mb: { xs: 1.5, sm: 1 },
                ml: 0,
                '& .MuiFormControlLabel-label': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { xs: '0.9375rem', sm: '1rem' }
                }
              }}
            />
          ))}
        </FormGroup>
      );
    }
    return renderChipList(selectedSessionTypes, sessionTypesLookups);
  };

  const renderAvailabilitySelect = () => {
    if (isEditing) {
      return (
        <FormGroup>
          {availabilityLookups.map((lookup) => (
            <FormControlLabel
              key={lookup.value}
              control={
                <Checkbox
                  checked={selectedAvailability.includes(lookup.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedAvailability([...selectedAvailability, lookup.value]);
                    } else {
                      setSelectedAvailability(selectedAvailability.filter(a => a !== lookup.value));
                    }
                  }}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-checked': {
                      color: 'rgba(100, 181, 246, 0.9)'
                    }
                  }}
                />
              }
              label={lookup.displayName}
              sx={{
                mb: { xs: 1.5, sm: 1 },
                ml: 0,
                '& .MuiFormControlLabel-label': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { xs: '0.9375rem', sm: '1rem' }
                }
              }}
            />
          ))}
        </FormGroup>
      );
    }
    return renderChipList(selectedAvailability, availabilityLookups);
  };

  const renderLanguagesSelect = () => {
    if (isEditing) {
      return (
        <FormGroup>
          {languageLookups.map((lookup) => (
            <FormControlLabel
              key={lookup.value}
              control={
                <Checkbox
                  checked={selectedLanguages.includes(lookup.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedLanguages([...selectedLanguages, lookup.value]);
                    } else {
                      setSelectedLanguages(selectedLanguages.filter(l => l !== lookup.value));
                    }
                  }}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-checked': {
                      color: 'rgba(100, 181, 246, 0.9)'
                    }
                  }}
                />
              }
              label={lookup.displayName}
              sx={{
                mb: { xs: 1.5, sm: 1 },
                ml: 0,
                '& .MuiFormControlLabel-label': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { xs: '0.9375rem', sm: '1rem' }
                }
              }}
            />
          ))}
        </FormGroup>
      );
    }
    return renderChipList(selectedLanguages, languageLookups);
  };

  const handleAddCustomTag = () => {
    const tag = customTagInput.trim();
    if (tag && !selectedExperienceOrTraining.includes(tag)) {
      setSelectedExperienceOrTraining([...selectedExperienceOrTraining, tag]);
      setCustomTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setSelectedExperienceOrTraining(selectedExperienceOrTraining.filter(t => t !== tag));
  };

  const renderExperienceOrTrainingEditor = () => {
    if (isEditing) {
      // Get lookup display names for matching
      const lookupValues = experienceTrainingLookups
        .filter(l => l.active !== false)
        .map(l => l.displayName);

      // Separate into lookup-based and custom tags
      const customTags = selectedExperienceOrTraining.filter(
        tag => !lookupValues.includes(tag)
      );

      return (
        <Box>
          {/* Checkboxes for admin-defined options */}
          <FormGroup>
            {experienceTrainingLookups
              .filter(l => l.active !== false)
              .map((lookup) => (
                <FormControlLabel
                  key={lookup.value}
                  control={
                    <Checkbox
                      checked={selectedExperienceOrTraining.includes(lookup.displayName)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedExperienceOrTraining([...selectedExperienceOrTraining, lookup.displayName]);
                        } else {
                          handleRemoveTag(lookup.displayName);
                        }
                      }}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-checked': {
                          color: 'rgba(156, 39, 176, 0.9)'
                        }
                      }}
                    />
                  }
                  label={lookup.displayName}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                />
              ))}
          </FormGroup>

          {/* Custom tags display */}
          {customTags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, mb: 1 }}>
              {customTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  onDelete={() => handleRemoveTag(tag)}
                  deleteIcon={<CancelIcon sx={{ color: 'rgba(255, 255, 255, 0.7) !important', fontSize: 16 }} />}
                  sx={{
                    backgroundColor: 'rgba(156, 39, 176, 0.2)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(156, 39, 176, 0.3)'
                  }}
                />
              ))}
            </Box>
          )}

          {/* Add custom tag input */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <TextField
              size="small"
              placeholder="Add custom tag..."
              value={customTagInput}
              onChange={(e) => setCustomTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomTag();
                }
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(156, 39, 176, 0.7)' }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            />
            <IconButton
              onClick={handleAddCustomTag}
              disabled={!customTagInput.trim()}
              sx={{ color: 'rgba(156, 39, 176, 0.9)', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </Box>
        </Box>
      );
    }

    // Read-only: show all tags as chips
    if (selectedExperienceOrTraining.length === 0) {
      return (
        <Typography variant="body2" className="theme-text-secondary">
          None specified
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selectedExperienceOrTraining.map((item: string, idx: number) => (
          <Chip
            key={idx}
            label={item}
            size="small"
            sx={{
              backgroundColor: 'rgba(156, 39, 176, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(156, 39, 176, 0.3)'
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <div className="glass-card p-6">
            <div className="flex justify-between items-center mb-6">
              <div></div>
              <div className="flex gap-4">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 text-sm font-bold theme-text-primary bg-white/10 border border-white/20 rounded-md shadow-sm hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 min-w-[160px] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 text-sm font-bold text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-w-[160px] transition-colors"
                    >
                      Save Changes
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 text-sm font-bold text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-w-[160px] transition-colors"
                  >
                    Edit Information
                  </button>
                )}
              </div>
            </div>
            <Grid container spacing={2}>
              {/* Completed Hours Summary */}
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    background: 'rgba(59, 130, 246, 0.15)',
                    border: '2px solid rgba(59, 130, 246, 0.4)',
                    borderRadius: '12px',
                    p: 3,
                    mb: 2
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600, mb: 2 }}>
                    Session Hours Summary
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '1rem' }}>
                      <strong>Total Completed Hours:</strong>
                    </Typography>
                    {loadingHours ? (
                      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Loading...
                      </Typography>
                    ) : (
                      <Box>
                        <Typography variant="h4" sx={{ color: 'rgba(255, 255, 255, 1)', fontWeight: 700 }}>
                          {completedHours} {completedHours === 1 ? 'hour' : 'hours'}
                        </Typography>
                        {previousSessions > 0 && (
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mt: 0.5 }}>
                            ({digitalSessions} digital + {previousSessions} previous)
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1.5, display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                    Based on completed sessions (1 hour per session)
                  </Typography>
                </Box>
              </Grid>

              {/* Divider */}
              <Grid item xs={12}>
                <Box sx={{ my: 1, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
              </Grid>

              {/* Experience Section */}
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>
                    Experience:
                  </Typography>
                  <Typography variant="body1" className="theme-text-primary" sx={{ flex: 1 }}>
                    {counsellor.application?.counselling?.experience || 'Not provided'}
                  </Typography>
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>
                    Previous Experience:
                  </Typography>
                  <Typography variant="body1" className="theme-text-primary" sx={{ flex: 1 }}>
                    {counsellor.application?.counselling?.previousExperience || 'Not provided'}
                  </Typography>
                </Typography>
              </Grid>

              {/* Experience or Training */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>
                    Experience/Training:
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {renderExperienceOrTrainingEditor()}
                  </Box>
                </Box>
              </Grid>

              {/* Professional Qualifications Section */}
              {counsellor.application?.counselling?.qualified === true && (
                <>
                  {/* Divider */}
                  <Grid item xs={12}>
                    <Box sx={{ my: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Qualification Status:</Typography>
                      <Typography variant="body1" className="theme-text-primary">
                        Qualified Counsellor
                        {counsellor.application.counselling.qualifiedYears && (
                          <> • {counsellor.application.counselling.qualifiedYears} years</>
                        )}
                      </Typography>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Client Hours:</Typography>
                      <Typography variant="body1" className={counsellor.application?.counselling?.clientHours ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application?.counselling?.clientHours || 'Not specified'}
                      </Typography>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Supervised Hours:</Typography>
                      <Typography variant="body1" className={counsellor.application?.counselling?.supervisedHours ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application?.counselling?.supervisedHours || 'Not specified'}
                      </Typography>
                    </Typography>
                  </Grid>
                </>
              )}

              {/* Clinical Supervisor Details - from existing counsellor onboarding, regular application, or legal form */}
              {(counsellor.application?.qualifications?.supervisorName || 
                counsellor.application?.legal?.supervisorName || 
                counsellor.application?.counselling?.ongoingSupervision) && (
                <>
                  {/* Divider */}
                  <Grid item xs={12}>
                    <Box sx={{ my: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" className="theme-text-primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Clinical Supervisor Details
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Supervisor Name:</Typography>
                      <Typography className={(counsellor.application?.qualifications?.supervisorName || counsellor.application?.legal?.supervisorName || counsellor.application?.counselling?.supervisorName) ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application?.qualifications?.supervisorName || counsellor.application?.legal?.supervisorName || counsellor.application?.counselling?.supervisorName || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Supervisor Phone:</Typography>
                      <Typography className={(counsellor.application?.qualifications?.supervisorPhoneNumber || counsellor.application?.legal?.supervisorPhoneNumber || counsellor.application?.counselling?.supervisorPhone) ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application?.qualifications?.supervisorPhoneNumber || counsellor.application?.legal?.supervisorPhoneNumber || counsellor.application?.counselling?.supervisorPhone || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>

                  {counsellor.application?.counselling?.supervisorEmail && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Supervisor Email:</Typography>
                        <Typography className="theme-text-primary">
                          {counsellor.application.counselling.supervisorEmail}
                        </Typography>
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              {/* BACP/NCPS Registration Number */}
              {(counsellor.application?.qualifications?.bacpNumber || counsellor.application?.legal?.bacpNumber) && (
                <>
                  {/* Divider */}
                  <Grid item xs={12}>
                    <Box sx={{ my: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" className="theme-text-primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Professional Registration
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>BACP/NCPS Number:</Typography>
                      <Typography className="theme-text-primary">
                        {counsellor.application?.qualifications?.bacpNumber || counsellor.application?.legal?.bacpNumber}
                      </Typography>
                    </Typography>
                  </Grid>
                </>
              )}

              {/* Qualification Status from Existing Counsellor Onboarding */}
              {counsellor.application?.qualifications?.qualified && (
                <>
                  {/* Divider */}
                  <Grid item xs={12}>
                    <Box sx={{ my: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" className="theme-text-primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Qualification Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Qualification Status:</Typography>
                      <Typography className="theme-text-primary">
                        Qualified Counsellor
                      </Typography>
                    </Typography>
                  </Grid>

                  {counsellor.application.qualifications.qualifications?.[0]?.completionDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Qualification Date:</Typography>
                        <Typography className="theme-text-primary">
                          {new Date(counsellor.application.qualifications.qualifications[0].completionDate).toLocaleDateString('en-GB')}
                        </Typography>
                      </Typography>
                    </Grid>
                  )}

                  {counsellor.application.qualifications.qualifications?.[0]?.name && (
                    <Grid item xs={12}>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>Qualification Details:</Typography>
                        <Typography className="theme-text-primary" sx={{ flex: 1 }}>
                          {counsellor.application.qualifications.qualifications[0].name}
                          {counsellor.application.qualifications.qualifications[0].institution && (
                            <> - {counsellor.application.qualifications.qualifications[0].institution}</>
                          )}
                        </Typography>
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              {/* Exit Date */}
              {counsellor.exitDate && (
                <>
                  {/* Divider */}
                  <Grid item xs={12}>
                    <Box sx={{ my: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Exit Date:</Typography>
                      <Typography className="theme-text-primary">
                        {new Date(counsellor.exitDate).toLocaleDateString('en-GB')}
                      </Typography>
                    </Typography>
                  </Grid>
                </>
              )}
              
              {/* Professional Membership */}
              {counsellor.application?.counselling?.qualified && counsellor.application?.counselling?.membershipBody && (
                <>
                  {/* Divider */}
                  <Grid item xs={12}>
                    <Box sx={{ my: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Membership Body:</Typography>
                      <Typography className={counsellor.application.counselling.membershipBody ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application.counselling.membershipBody || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Membership Type:</Typography>
                      <Typography className={counsellor.application.counselling.membershipType ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application.counselling.membershipType || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Membership Number:</Typography>
                      <Typography className={counsellor.application.counselling.membershipNumber ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application.counselling.membershipNumber || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Expiry Date:</Typography>
                      <Typography className={counsellor.application.counselling.membershipExpiryDate ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application.counselling.membershipExpiryDate
                          ? new Date(counsellor.application.counselling.membershipExpiryDate).toLocaleDateString('en-GB')
                          : 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                </>
              )}
              
              {/* Insurance Information */}
              {counsellor.application?.counselling?.qualified && counsellor.application?.counselling?.insuranceProvider && (
                <>
                  {/* Divider */}
                  <Grid item xs={12}>
                    <Box sx={{ my: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Insurance Provider:</Typography>
                      <Typography className={counsellor.application.counselling.insuranceProvider ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application.counselling.insuranceProvider || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Coverage:</Typography>
                      <Typography className={counsellor.application.counselling.insuranceCoverage ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application.counselling.insuranceCoverage || 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px' }}>Expiry Date:</Typography>
                      <Typography className={counsellor.application.counselling.insuranceExpiryDate ? 'theme-text-primary' : 'theme-text-secondary'}>
                        {counsellor.application.counselling.insuranceExpiryDate
                          ? new Date(counsellor.application.counselling.insuranceExpiryDate).toLocaleDateString('en-GB')
                          : 'Not provided'}
                      </Typography>
                    </Typography>
                  </Grid>
                </>
              )}
              
              {/* Current Professional Settings */}
              {/* Divider */}
              <Grid item xs={12}>
                <Box sx={{ my: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }} />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 }, minWidth: { xs: 'auto', sm: '180px' }, flexShrink: 0 }}>
                    Modality:
                  </Typography>
                  <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 0.5 } }}>
                    {renderModalitySelect()}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 }, minWidth: { xs: 'auto', sm: '180px' }, flexShrink: 0 }}>
                    Session Types:
                  </Typography>
                  <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 0.5 } }}>
                    {renderSessionTypesSelect()}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 }, minWidth: { xs: 'auto', sm: '180px' }, flexShrink: 0 }}>
                    Languages:
                  </Typography>
                  <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 0.5 } }}>
                    {renderLanguagesSelect()}
                  </Box>
                </Box>
              </Grid>

              {/* Availability Time Slots */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: 1, minWidth: '180px', flexShrink: 0 }}>
                    Availability Time Slots:
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {!isEditing && (
                      <Typography variant="body2" className="theme-text-secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        Specific days and times when available for sessions
                      </Typography>
                    )}
                    <AvailabilitySlotEditor
                      slots={availabilitySlots}
                      onChange={setAvailabilitySlots}
                      readOnly={!isEditing}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* General Availability */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" fontWeight="bold" className="theme-text-primary" sx={{ mr: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 }, minWidth: { xs: 'auto', sm: '180px' }, flexShrink: 0 }}>
                    General Availability (Optional):
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {!isEditing && (
                      <Typography variant="body2" className="theme-text-secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                        General preferences - use time slots above for specific scheduling
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 0.5 } }}>
                      {renderAvailabilitySelect()}
                    </Box>
                  </Box>
                </Box>
              </Grid>

            </Grid>
          </div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfessionalInformation;
